/* eslint-disable react/jsx-props-no-spreading */
import {
  QuestionCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons'
import { Form, Col, Divider, Popover, Row, Alert } from 'antd'
import { Formik } from 'formik'
import mapValues from 'lodash/mapValues'
import dayjs from 'dayjs'
import PropTypes from 'prop-types'
import { useMemo, useState, useEffect, useCallback } from 'react'
import { createUseStyles, useTheme } from 'react-jss'
import { useDispatch } from 'react-redux'

import {
  HOME_MONITOR_MODELS,
  PATIENTS_PLACEHOLDERS,
  PATIENTS_LABELS,
} from '../../lib/constants'
import { formElementPropsGenerator } from '../../lib/forms'
import api from '../../api'
import { Yup } from '../../lib/validations'
import {
  useIsAdmin,
  useFeatureEnabled,
  useCurrentUserPracticeId,
} from '../../redux/slices/session'
import {
  useOfficeLocations,
  fetchOfficeLocations,
} from '../../redux/slices/office-locations'
import {
  Checkbox,
  DatePicker,
  Input,
  PhoneNumber,
  SubmitButton,
} from '../Forms'
import CountrySelect from '../Forms/CountrySelect'
import InsuranceProviderSelect from '../Forms/InsuranceProviderSelect'
import MedicalProviderSelect from '../Forms/MedicalProviderSelect'
import DeviceModelSelect from '../Forms/DeviceModelSelect'
import PracticeSelect from '../Forms/PracticeSelect'
import Switch, { VALUE_LABELS_SCHEMA } from '../Forms/Switch'
import USStateSelect from '../Forms/USStateSelect'
import { preFilledPatientShape, patientShape } from '../prop-types'
import IcdCodesSelect from '../Forms/IcdCodesSelect'
import Select from '../Forms/Select'
import useDiseases from '../../hooks/useDiseases'
import { useIsMobile } from '../../hooks'
import MultipleCheckboxes from '../Forms/MultipleCheckboxes'
import OfficeLocationSelect from '../Forms/OfficeLocationSelect'
import DiseasesEdit from './DiseasesEdit'

const useStyles = createUseStyles({
  questions: {
    '& .ant-form-item-control-wrapper': {
      display: 'inline-block',
    },
    '& .ant-form-item-label': {
      display: 'inline-block',
      verticalAlign: 'inherit',
    },
    '& .ant-form-extra': {
      display: 'inline-block',
      marginLeft: 10,
    },
  },
})

const INITIAL_VALUES = mapValues(PATIENTS_LABELS, () => '')
INITIAL_VALUES.practiceId = undefined
INITIAL_VALUES.insuranceProviderId = undefined
INITIAL_VALUES.medicalProviderId = undefined
INITIAL_VALUES.birthDate = undefined
INITIAL_VALUES.addressState = undefined
INITIAL_VALUES.addressCountry = 'United States'
INITIAL_VALUES.atrialFibrilationFlutterDisease = false
INITIAL_VALUES.anticoagulatedDisease = false
INITIAL_VALUES.alertForAnyAfib = false
INITIAL_VALUES.oacContraindicated = false
INITIAL_VALUES.enrolled = false
INITIAL_VALUES.paired = false
INITIAL_VALUES.currentDeviceAttributes = {
  deviceModelId: undefined,
  serialNumber: '',
}

INITIAL_VALUES.icdCodeIds = []
INITIAL_VALUES.alertRecipientIds = []
INITIAL_VALUES.practiceMrn = undefined
INITIAL_VALUES.deviceIcdCodeId = undefined
INITIAL_VALUES.remoteMonitoring = true
INITIAL_VALUES.diseaseIds = []

const DEFAULT_DOB = dayjs().subtract(40, 'years')

const propsFor = formElementPropsGenerator({
  formName: 'patient-form',
  labels: PATIENTS_LABELS,
  placeholders: PATIENTS_PLACEHOLDERS,
})

function buildValidationSchema(isNew = false, forAdmin = false) {
  const shape = {
    firstName: Yup.string().min(2).required(),
    lastName: Yup.string().min(2).required(),
    birthDate: Yup.date().max(new Date()).required(),
    email: Yup.string().nullable().email(),
    mobilePhone: Yup.string()
      .nullable()
      .phone()
      .test(
        'phone-required',
        'Either mobile phone or home phone must be provided',
        function validatePhoneRequired(value) {
          const { homePhone } = this.parent
          return value || homePhone
        },
      ),
    homePhone: Yup.string()
      .nullable()
      .phone()
      .test(
        'phone-required',
        'Either mobile phone or home phone must be provided',
        function validatePhoneRequired(value) {
          const { mobilePhone } = this.parent
          return !!(value || mobilePhone)
        },
      ),
    addressStreet: Yup.string().required(),
    addressApt: Yup.string().nullable(),
    addressCity: Yup.string().required(),
    addressState: Yup.string().when('addressCountry', {
      is: value => value === 'United States',
      then: schema => schema.required(),
      otherwise: schema => schema.nullable(),
    }),
    addressCountry: Yup.string(),
    addressZipCode: Yup.string().required(),
    homeMonitorSerialNumber: Yup.string().nullable(),
    remoteMonitoring: Yup.bool().default(true),
    diseaseIds: Yup.array(),
    medicalProviderId: Yup.string().nullable(),
    insuranceProviderId: Yup.string().nullable(),
    officeLocationId: Yup.string(),
  }
  if (isNew) {
    shape.currentDeviceAttributes = Yup.object().shape({
      deviceModelId: Yup.string().required(),
      serialNumber: Yup.string().required(),
    })
  }
  if (forAdmin && isNew) {
    shape.practiceId = Yup.string().required()
  }
  return Yup.object().shape(shape)
}

// Keyed on `md`, not `sm`: below 768 the app is in its mobile layout, and a
// side-by-side label leaves the input about 200px wide.
const labelCol = {
  md: { span: 8 },
}

const wrapperCol = {
  md: { span: 15 },
}

const actionsWrapperCol = {
  md: { span: 4, offset: 10 },
}

const devicePopupContent = (
  <div>
    It can be found at:
    <ul>
      <li>An older device interrogation</li>
      <li>Patients device ID CARD</li>
      <li>By doing an in-office device interrogation</li>
    </ul>
  </div>
)

const formatPatientData = patientData => {
  const { insuranceProviderId, deviceIcdCode, ...rest } = patientData

  if (!patientData.remoteMonitoring) {
    rest.enrolled = false
    rest.paired = false
    rest.homeMonitorSerialNumber = null
  }

  const values = {
    ...rest,
    oacContraindicated:
      patientData?.oacContraindicated ?? INITIAL_VALUES.oacContraindicated,
    alertRecipientIds: patientData.alertRecipients
      ? patientData.alertRecipients.map(recipient => recipient.id.toString())
      : [],
    icdCodeIds: patientData.icdCodes
      ? patientData.icdCodes.map(code => code.id.toString())
      : [],
    deviceIcdCodeId:
      deviceIcdCode?.id || INITIAL_VALUES.deviceIcdCodeId,
    insuranceProviderId:
      patientData?.insuranceProvider?.id || INITIAL_VALUES.insuranceProviderId,
    medicalProviderId:
      patientData?.medicalProvider?.id || INITIAL_VALUES.medicalProviderId,
  }

  if (patientData?.officeLocation?.id) {
    values.officeLocationId = String(patientData.officeLocation.id)
  }

  return values
}

export default function PatientForm({
  onSubmit,
  preFilled = {},
  patientData = undefined,
 }) {
  const classes = useStyles()
  const isMobile = useIsMobile()
  const initialValues = patientData
    ? formatPatientData(patientData)
    : { ...INITIAL_VALUES, ...preFilled }
  const isAdmin = useIsAdmin()
  const inOfficeChecksEnabled = useFeatureEnabled('in_office_checks')
  const validationSchema = useMemo(
    () => buildValidationSchema(!patientData, isAdmin),
    [isAdmin, patientData],
  )

  const [filteredICDCodes, setFilteredICDCodes] = useState([])
  const diseases = useDiseases()

  const onSelectDeviceModel = value => {
    api.icdCodes.getIcdCodes('all', { device_model_id: value }).then(list => {
      setFilteredICDCodes(list)
    })
  }
  useEffect(() => {
    if (patientData?.currentDevice)
      api.icdCodes
        .getIcdCodes('all', { patient_device_id: patientData.currentDevice.id })
        .then(list => {
          setFilteredICDCodes(list)
        })
  }, [patientData])

  const icdItem = code => ({
    value: code.id,
    label: `${code.code} - ${code.description}`,
  })

  const DiseasesForm = useCallback(() => {
    if (patientData) {
      return <DiseasesEdit patientId={patientData.id} />
    }
    return (
      <MultipleCheckboxes
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...propsFor('diseaseIds')}
        options={diseases.map(disease => ({
          label: disease.name,
          value: disease.id,
        }))}
        wrapperCol={{ md: { span: 21 } }}
        labelCol={{ md: { span: 3 } }}
        labelAlign="left"
      />
    )
  }, [diseases, patientData])

  const currentUserPracticeId = useCurrentUserPracticeId()
  const [currentPracticeId, setCurrentPracticeId] = useState(
    patientData?.practiceId || currentUserPracticeId,
  )
  const dispatch = useDispatch()
  const officeLocations = useOfficeLocations(currentPracticeId)
  useEffect(() => {
    if (currentPracticeId) {
      const fetchLocations = fetchOfficeLocations(currentPracticeId)
      fetchLocations(dispatch)
    }
  }, [currentPracticeId, dispatch])
  const { brandPrimary } = useTheme()

  const onPracticeChange = useCallback(
    practiceId => {
      if (practiceId && practiceId !== currentPracticeId) {
        setCurrentPracticeId(practiceId)
      }
    },
    [currentPracticeId],
  )

  return (
    <div>
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
      >
        {({
          handleSubmit,
          isSubmitting,
          isValid,
          submitCount,
          values,
          errors,
        }) => {
          const practiceId =
            patientData?.practiceId ||
            values?.practiceId ||
            currentUserPracticeId

          return (
            <Form
              // Vertical below md: side by side, the label eats a third of a
              // phone and antd keeps it right-aligned against a left-aligned
              // input. Dropping the cols lets the vertical layout own the row.
              layout={isMobile ? 'vertical' : 'horizontal'}
              labelCol={isMobile ? undefined : labelCol}
              wrapperCol={isMobile ? undefined : wrapperCol}
              onFinish={handleSubmit}

            >
              {' '}
              {Object.keys(errors).length > 0 && submitCount > 0 && (
                <Alert
                  message="Please check the fields with errors."
                  type="error"
                />
              )}
              {isAdmin && !patientData && (
                <Col xs={24} md={12}>
                  <Form.Item
                    labelCol={{ md: { span: 8 } }}
                    label={PATIENTS_LABELS.practiceId}
                    validateStatus={
                      submitCount > 0 && errors.practiceId ? 'error' : ''
                    }
                    help={
                      submitCount > 0 && errors.practiceId
                        ? 'Please select a practice'
                        : undefined
                    }
                  >
                    <PracticeSelect
                      {...propsFor('practiceId')}
                      autoFocus
                      label=""
                      additionalChangeListener={onPracticeChange}
                      wrapperItem={false}
                    />
                  </Form.Item>
                </Col>
              )}
              <Divider orientation="left">Personal Information</Divider>
              <Row>
                <Col xs={24} md={12}>
                  <Input {...propsFor('firstName')} autoFocus={!isAdmin} />
                </Col>
                <Col xs={24} md={12}>
                  <Input {...propsFor('lastName')} />
                </Col>
                <Col xs={24} md={12}>
                  <DatePicker
                    {...propsFor('birthDate')}
                    allowClear={false}
                    defaultPickerValue={DEFAULT_DOB}
                  />
                </Col>
                <Col xs={24} md={12}>
                  <Input {...propsFor('email')} />
                </Col>
              </Row>
              <Row>
                <Col xs={24} md={12}>
                  <PhoneNumber {...propsFor('mobilePhone')} />
                </Col>
                <Col xs={24} md={12}>
                  <PhoneNumber {...propsFor('homePhone')} />
                </Col>
              </Row>
              <Row>
                <Col xs={24} md={12}>
                  <Input {...propsFor('practiceMrn')} />
                </Col>
              </Row>
              {inOfficeChecksEnabled && (
                <Row>
                  {/* The offset belongs to the desktop grid; left unscoped it
                      pushes a full-width column off the side of the page. */}
                  <Col xs={24} md={{ span: 8, offset: 4 }}>
                    <Checkbox
                      {...propsFor('inOfficeChecks')}
                      checked
                      disabled
                    />
                  </Col>
                  <Col xs={24} md={8}>
                    <Checkbox {...propsFor('remoteMonitoring')} />
                  </Col>
                </Row>
              )}
              <Divider orientation="left">Address</Divider>
              <Row>
                <Col xs={24} md={12}>
                  <Input {...propsFor('addressStreet')} />
                </Col>
                <Col xs={24} md={12}>
                  <Input {...propsFor('addressApt')} />
                </Col>
                <Col xs={24} md={12}>
                  <Input {...propsFor('addressCity')} />
                </Col>
                <Col xs={24} md={12}>
                  <USStateSelect {...propsFor('addressState')} />
                </Col>
                <Col xs={24} md={12}>
                  <CountrySelect {...propsFor('addressCountry')} />
                </Col>
                <Col xs={24} md={12}>
                  <Input {...propsFor('addressZipCode')} />
                </Col>
              </Row>
              <Divider orientation="left">Insurance Information</Divider>
              <Row>
                <Col xs={24} md={12}>
                  <Form.Item
                    labelCol={{ md: { span: 8 } }}
                    label={PATIENTS_LABELS.insuranceProviderId}
                  >
                    <InsuranceProviderSelect
                      {...propsFor('insuranceProviderId')}
                      label=""
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Input {...propsFor('insurancePolicyNumber')} />
                </Col>
              </Row>
              <Divider orientation="left">Medical Providers</Divider>
              <Row>
                <Col xs={24} md={12}>
                  <Form.Item
                    labelCol={{ md: { span: 8 } }}
                    label={PATIENTS_LABELS.medicalProviderId}
                  >
                    {practiceId && (
                      <MedicalProviderSelect
                        {...propsFor('medicalProviderId')}
                        practiceId={practiceId}
                        displayNPI
                        label=""
                      />
                    )}
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Col span={24}>
                    <Input {...propsFor('pcpName')} />
                  </Col>
                  <Col span={24}>
                    <Input {...propsFor('pcpPhoneNumber')} />
                  </Col>
                  <Col span={24}>
                    <Input {...propsFor('pcpFaxNumber')} />
                  </Col>
                  {practiceId && (
                    <Col span={24}>
                      <Form.Item
                        labelCol={{ md: { span: 8 } }}
                        label={PATIENTS_LABELS.alertsRecipientId}
                      >
                        <MedicalProviderSelect
                          {...propsFor('alertRecipientIds')}
                          label=""
                          practiceId={practiceId}
                          mode="multiple"
                          alertRecipients
                        />
                      </Form.Item>
                    </Col>
                  )}
                </Col>
              </Row>
              {officeLocations?.length > 0 && (
                <Row>
                  <Col xs={24} md={12}>
                    <Form.Item
                      labelCol={{ md: { span: 8 } }}
                      label={PATIENTS_LABELS.officeLocationId}
                    >
                      {practiceId && (
                        <OfficeLocationSelect
                          {...propsFor('officeLocationId')}
                          practiceId={practiceId}
                          label=""
                        />
                      )}
                    </Form.Item>
                  </Col>
                </Row>
              )}
              <Divider orientation="left">Cardiac Device</Divider>
              <>
                {!patientData && (
                  <Row>
                    <Col xs={24} md={12}>
                        <DeviceModelSelect
                          {...propsFor('currentDeviceAttributes.deviceModelId')}
                          wrapperItem
                          labelCol={{ md: { span: 8 } }}
                          label={<Popover content={devicePopupContent}>
                            {
                              PATIENTS_LABELS.currentDeviceAttributes
                                .deviceModelId
                            }{' '}
                            <QuestionCircleOutlined />
                          </Popover>}
                          onSelect={onSelectDeviceModel}
                        />
                    </Col>
                    <Col xs={24} md={12}>
                      <Input
                        {...propsFor('currentDeviceAttributes.serialNumber')}
                        label={
                          <Popover content={devicePopupContent}>
                            {
                              PATIENTS_LABELS.currentDeviceAttributes
                                .serialNumber
                            }{' '}
                            <QuestionCircleOutlined />
                          </Popover>
                        }
                      />
                    </Col>
                  </Row>
                )}
                {filteredICDCodes.length > 0 && (
                  <Row>
                    <Col xs={24} md={16}>
                      <IcdCodesSelect
                        // eslint-disable-next-line react/jsx-props-no-spreading
                        {...propsFor('icdCodeIds')}
                        labelCol={{ md: { span: 6 } }}
                        wrapperCol={{ md: { span: 12 } }}
                        wrapperItem
                        label="ICD Code for 93297 and 93298"
                        id="patient-form-deviceIcdCodesId"
                        name="deviceIcdCodeId"
                        options={filteredICDCodes.map(icdItem)}
                        required
                      />
                    </Col>
                  </Row>
                )}
              </>
              {!values.remoteMonitoring && (
                <Row>
                  <Col xs={24} md={12} style={{ color: brandPrimary }}>
                    <ExclamationCircleOutlined style={{ marginRight: 5 }} />{' '}
                    Remote monitoring is not enabled
                  </Col>
                </Row>
              )}
              {!!values.remoteMonitoring && (
                <Row className={classes.questions}>
                  <Col span={24}>
                    <Switch
                      {...propsFor('enrolled')}
                      leftAlign
                      valueLabelsSchema={VALUE_LABELS_SCHEMA.yesNo}
                    />
                  </Col>
                  <Col span={24}>
                    <Switch
                      {...propsFor('paired')}
                      leftAlign
                      valueLabelsSchema={VALUE_LABELS_SCHEMA.yesNo}
                    />
                  </Col>
                  <Col xs={24} md={16}>
                    <Select
                      {...propsFor('homeMonitorSerialNumber')}
                      wrapperItem
                      labelCol={{ md: { span: 6 } }}
                      wrapperCol={{ md: { span: 12 } }}
                      hasGroups
                      options={HOME_MONITOR_MODELS}
                      label={
                      <Popover content="It can be found on the box or the back of the home monitor.">
                        {PATIENTS_LABELS.homeMonitorSerialNumber}{' '}
                        <QuestionCircleOutlined />
                      </Popover>
                    }
                    />
                  </Col>
                </Row>
              )}
              <Divider orientation="left">Diseases and History</Divider>
              <DiseasesForm />
              <Row style={{ marginTop: 15 }}>
                <Col xs={24} md={6}>
                  <Checkbox
                    {...propsFor('alertForAnyAfib')}
                    label="Alert for Any AFib"
                  />
                </Col>
                <Col xs={24} md={6}>
                  <Checkbox
                    {...propsFor('oacContraindicated')}
                    label="OAC Contraindicated"
                  />
                </Col>
              </Row>
              <Row>
                <Col xs={24} md={16}>
                  <IcdCodesSelect
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...propsFor('icdCodeIds')}
                    labelCol={{ md: { span: 6 } }}
                    wrapperCol={{ md: { span: 16 } }}
                    mode="multiple"
                  />
                </Col>
              </Row>
              <SubmitButton
                isSubmitting={isSubmitting}
                isValid={isValid}
                title={patientData ? 'Save Patient' : 'Create Patient'}
                submitCount={submitCount}
                wrapperCol={actionsWrapperCol}
              />
            </Form>
          )
        }}
      </Formik>

      <style>
        {`
          .has-error.has-feedback .ant-form-item-children-icon {
            display: none;
          }
        `}
      </style>
    </div>
  )
}

PatientForm.propTypes = {
  preFilled: preFilledPatientShape,
  patientData: patientShape,
  onSubmit: PropTypes.func.isRequired,
}
